import { EmojiEvents, Leaderboard, SentimentDissatisfied, TrackChanges, Warning } from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SCROLL_TARGETS } from "../../../common/constants";
import { FeatureFlag } from "../../../common/posthog";
import { AutoGenerateParams, ReportRequestedParams } from "../types";
import FindBestWorstCallsWizard from "../../explore/components/find-best-worst/FindBestWorstCallsWizard";
import WeeklyCoachingWizard from "./weekly-coaching-wizard/WeeklyCoachingWizard";

interface ActionTile {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  featureFlag?: string;
}

const tileIconSx = { fontSize: 28, color: "white" };

const actionTiles: ActionTile[] = [
  {
    id: "weekly-coaching",
    title: "Weekly Coaching Guide",
    description: "Generate personalized coaching insights for individual team members with actionable recommendations and conversation examples.",
    icon: <TrackChanges sx={tileIconSx} />
  },
  {
    id: "review-negative-calls",
    title: "Review Yesterday's Negative Calls",
    description: "Explore conversations with negative sentiment from yesterday to identify coaching opportunities and support needs.",
    icon: <SentimentDissatisfied sx={tileIconSx} />
  },
  {
    id: "review-high-risk-calls",
    title: "Review Yesterday's High Risk Calls",
    description: "Jump to Explorer view filtered for high-risk conversations from yesterday that may need immediate attention or coaching.",
    icon: <Warning sx={tileIconSx} />
  },
  {
    id: "monthly-team-leaderboard",
    title: "Monthly Team Leaderboard",
    description: "See how your team is performing on the team leaderboard for the last 30 days.",
    icon: <Leaderboard sx={tileIconSx} />
  },
  {
    id: "find-best-worst-calls",
    title: "Find Best & Worst Calls",
    description: "Pinpoint your strongest and weakest calls for any lens or attribute",
    icon: <EmojiEvents sx={tileIconSx} />,
    featureFlag: "find-best-worst-calls"
  }
];

const containerSx = { pb: 3 };

const cardSx = {
  height: "100%",
  border: "1px solid lightgrey",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: 4
  }
};

const cardActionAreaSx = { height: "100%", p: 3 };

const cardContentSx = { p: 0, textAlign: "center" };

const iconWrapperSx = {
  width: 48,
  height: 48,
  borderRadius: 2,
  backgroundColor: "primary.main",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 2,
  mx: "auto"
};

const titleSx = { fontWeight: 600, mb: 1 };

interface QuickActionsProps {
  onReportRequested?: (params: ReportRequestedParams) => void;
  autoGenerateParams?: AutoGenerateParams | null;
  onAutoGenerateComplete?: () => void;
}

interface ExplorerNavigationParams {
  timeRange: string;
  sentiment?: string;
  risk?: string;
  artifact: string;
}

const QuickActions = ({ onReportRequested, autoGenerateParams, onAutoGenerateComplete }: QuickActionsProps) => {
  const navigate = useNavigate();
  const [weeklyWizardOpen, setWeeklyWizardOpen] = useState(false);
  const [findBestWorstWizardOpen, setFindBestWorstWizardOpen] = useState(false);

  useEffect(() => {
    if (autoGenerateParams) {
      setWeeklyWizardOpen(true);
    }
  }, [autoGenerateParams]);

  const navigateToExplorer = (params: ExplorerNavigationParams) => {
    const searchParams = new URLSearchParams();
    searchParams.set("time_range", params.timeRange);
    if (params.sentiment) {
      searchParams.set("sentiment", params.sentiment);
    }
    if (params.risk) {
      searchParams.set("ra_overall_indicator", params.risk);
    }
    searchParams.set("auto_open", "first");
    searchParams.set("artifact", params.artifact);
    navigate(`/explore?${searchParams.toString()}`);
  };

  const handleMonthlyTeamLeaderboard = () => {
    const searchParams = new URLSearchParams();
    searchParams.set("time_range", "last30Days");
    searchParams.set("user_email", "all");
    searchParams.set("sc_name", "all");
    searchParams.set("scroll_to", SCROLL_TARGETS.TEAM_LEADERBOARD);
    navigate(`/performance-analysis?${searchParams.toString()}`);
  };

  const handleTileClick = (tileId: string) => {
    const actionHandlers: Record<string, () => void> = {
      "weekly-coaching": () => setWeeklyWizardOpen(true),
      "find-best-worst-calls": () => setFindBestWorstWizardOpen(true),
      "review-negative-calls": () => navigateToExplorer({
        timeRange: "yesterday",
        sentiment: "negative",
        artifact: "summary"
      }),
      "review-high-risk-calls": () => navigateToExplorer({
        timeRange: "yesterday",
        risk: "high",
        artifact: "risk"
      }),
      "monthly-team-leaderboard": handleMonthlyTeamLeaderboard
    };

    const handler = actionHandlers[tileId];
    if (handler) {
      handler();
    }
  };

  return (
    <Box sx={containerSx}>
      <Grid container spacing={3}>
        {actionTiles.map((tile) => {
          const tileContent = (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tile.id}>
              <Card sx={cardSx}>
                <CardActionArea
                  onClick={() => handleTileClick(tile.id)}
                  sx={cardActionAreaSx}
                >
                  <CardContent sx={cardContentSx}>
                    <Box sx={iconWrapperSx}>
                      {tile.icon}
                    </Box>
                    <Typography variant="h6" sx={titleSx}>
                      {tile.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tile.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );

          if (tile.featureFlag) {
            return (
              <FeatureFlag key={tile.id} flag={tile.featureFlag}>
                {tileContent}
              </FeatureFlag>
            );
          }

          return tileContent;
        })}
      </Grid>

      <WeeklyCoachingWizard
        open={weeklyWizardOpen}
        onClose={() => {
          setWeeklyWizardOpen(false);
          onAutoGenerateComplete?.();
        }}
        onReportRequested={onReportRequested}
        autoGenerateParams={autoGenerateParams}
      />

      <FindBestWorstCallsWizard
        open={findBestWorstWizardOpen}
        onClose={() => setFindBestWorstWizardOpen(false)}
      />
    </Box>
  );
};

export default QuickActions;
